import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Phone, ArrowRight, CheckCircle, Wrench, Droplet, Flame, BadgeAlert, Search, ShieldCheck, Waves, Snowflake, Bath, MapPin } from "lucide-react";
import { CanonicalLink as Link } from "@/components/CanonicalLink";
import { QuoteModal } from "@/components/QuoteModal";
import { SEOHead } from "@/components/SEOHead";
import { JsonLd } from "@/components/JsonLd";
import { services } from "@/data/services";
import { homeFaqs } from "@/data/informative-faqs";
import { BUSINESS_NAME, BUSINESS_ADDRESS_LABEL, BUSINESS_MAP_URL, BUSINESS_PHONE, BUSINESS_PHONE_DISPLAY, BUSINESS_HOURS_LABEL, businessSchema } from "@/lib/seo";
import { GoogleReviewsTrust } from "@/components/GoogleReviewsTrust";
import { GoogleHeroBadge } from "@/components/GoogleHeroBadge";
import { NativeFaqList } from "@/components/NativeFaqList";
import { AdditionalServices } from "@/components/AdditionalServices";
import { ServiceAreas } from "@/components/ServiceAreas";
import { submitContactRequest, contactRequestErrorMessage, reportContactConversion } from "@/lib/contact-request";
import plumberSmall from "@/assets/images/plumber-working-480.webp";
import plumberLarge from "@/assets/images/plumber-working-772.webp";
import { BusinessMap } from "@/components/BusinessMap";

const businessAddressLabel = BUSINESS_ADDRESS_LABEL;
const businessMapUrl = BUSINESS_MAP_URL;

export default function Home() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [homeTerms, setHomeTerms] = useState(false);
  const [homeTermsError, setHomeTermsError] = useState(false);
  const [homeSubmitted, setHomeSubmitted] = useState(false);
  const [homeLoading, setHomeLoading] = useState(false);
  const [homeName, setHomeName] = useState("");
  const [homePhone, setHomePhone] = useState("");
  const [homeMessage, setHomeMessage] = useState("");
  const [homeSubmitError, setHomeSubmitError] = useState("");

  async function handleHomeSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (homeLoading) return;
    if (!homeTerms) {
      setHomeSubmitError("Veuillez accepter les termes et conditions avant d’envoyer votre demande.");
      return;
    }
    setHomeSubmitError("");
    setHomeLoading(true);
    try {
      await submitContactRequest({ name: homeName, phone: homePhone, message: homeMessage, source: "accueil", terms: homeTerms });
      setHomeSubmitted(true);
      reportContactConversion();
    } catch (error) {
      setHomeSubmitError(contactRequestErrorMessage(error));
    } finally {
      setHomeLoading(false);
    }
  }

  return (
    <div className="w-full">
      <SEOHead
        title="Plombier Longueuil 24/7 | Plomberie Excellence"
        description="Plombier à Longueuil, disponible 24/7 pour vos urgences, débouchages, chauffe-eau et fuites. Demandez une soumission ou appelez-nous au (438) 908-1143."
        canonical="https://plomberieexcellence.com"
        faqs={homeFaqs}
      />
      <JsonLd schema={businessSchema} />

      {/* 1. HERO SECTION */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-32 overflow-hidden bg-primary text-white min-h-[580px] md:min-h-[680px]">
        {/* Image équipe — droite, fade vers la gauche */}
        <div className="absolute inset-y-0 right-0 w-full md:w-[65%] lg:w-[55%]">
          <img
            src={plumberLarge}
            srcSet={`${plumberSmall} 480w, ${plumberLarge} 772w`}
            sizes="(min-width: 1024px) 55vw, (min-width: 768px) 65vw, 100vw"
            alt="Équipe de plombiers professionnels"
            width={772}
            height={1449}
            fetchPriority="high"
            loading="eager"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-primary/75 md:hidden" aria-hidden="true" />
          {/* Fade de droite vers gauche */}
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(to right, hsl(215 100% 20%) 0%, hsl(215 100% 20% / 0.85) 15%, hsl(215 100% 20% / 0.4) 50%, transparent 100%)" }}
          />
          {/* Fade bas */}
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-primary to-transparent" />
        </div>

        {/* Contenu texte */}
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="max-w-xl">
            <div>
              <Badge variant="outline" className="bg-white/10 text-white border-white/20 mb-6 py-1.5 px-4 text-sm">
                <span className="w-2 h-2 rounded-full bg-accent animate-pulse mr-2"></span>
                Plombier Rive-Sud — Urgence 24/7
              </Badge>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6 leading-tight">
                Plombier à <span className="text-accent-on-dark">Longueuil</span><br className="hidden md:block"/>
                {" "}disponible en tout temps
              </h1>
              <p className="text-lg md:text-xl text-white/80 mb-10 leading-relaxed">
                Notre équipe de plombiers certifiés et professionnels intervient à Longueuil, à Saint-Hubert et à Greenfield Park pour vos urgences et vos projets de plomberie planifiés.
              </p>
            </div>

            <GoogleHeroBadge className="mb-6" />

            <div
              className="flex flex-col sm:flex-row gap-4"
            >
              <a href="tel:4389081143" className="w-full sm:w-auto inline-flex items-center justify-center bg-accent hover:bg-accent/90 text-white font-bold text-lg h-14 px-8 rounded-md shadow-xl hover:-translate-y-1 transition-all">
                <Phone className="mr-2 h-5 w-5" />
                Appeler maintenant
              </a>
              <Button
                size="lg"
                variant="outline"
                onClick={() => setQuoteOpen(true)}
                className="w-full sm:w-auto bg-white/10 border-white/20 hover:bg-white hover:text-primary text-white font-bold text-lg h-14 px-8 backdrop-blur-sm transition-all"
              >
                Obtenir une soumission
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* 2. URGENCE 24/7 BLOCK */}
      <section className="bg-accent/10 border-y border-accent/20">
        <div className="container mx-auto px-4 py-8 md:py-14">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6 md:gap-8 bg-white p-6 md:p-10 rounded-2xl shadow-xl border border-accent/10 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-accent/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
            
            <div className="flex-1 relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <BadgeAlert className="h-8 w-8 text-accent animate-pulse" />
                <h2 className="text-2xl md:text-3xl font-bold text-primary">Urgence plomberie 24/7</h2>
              </div>
              <p className="text-muted-foreground text-base md:text-lg mb-4 md:mb-6">Ne laissez pas un dégât d'eau empirer. Notre équipe d'urgence est disponible pour vous aider avec :</p>
              <ul className="grid sm:grid-cols-2 gap-3 mb-0">
                {['Fuite d\'eau majeure', 'Tuyau éclaté ou gelé', 'Refoulement d\'égout', 'Chauffe-eau défectueux'].map((item) => (
                  <li key={item} className="flex items-center gap-2 font-medium text-foreground">
                    <div className="w-1.5 h-1.5 rounded-full bg-accent"></div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="w-full lg:w-auto relative z-10">
              <a href="tel:4389081143" className="block w-full">
                <div className="bg-accent p-6 rounded-xl shadow-lg hover:shadow-xl transition-all group cursor-pointer text-center border-2 border-transparent hover:border-white/20">
                  <p className="text-white/90 text-sm font-bold uppercase tracking-wider mb-2">Service d'urgence 24/7</p>
                  <p className="text-white text-2xl md:text-4xl font-black mb-2">(438) 908-1143</p>
                  <div className="flex items-center justify-center gap-2 text-white font-medium group-hover:translate-x-1 transition-transform">
                    Nous sommes en route <ArrowRight className="h-5 w-5" />
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 3. SERVICES OVERVIEW */}
      <section className="py-20 md:py-32 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">Nos services de plomberie</h2>
            <p className="text-lg text-muted-foreground">Urgence ou projet planifié : trouvez le service adapté à votre problème et contactez notre équipe.</p>
          </div>

          {(() => {
            const serviceIconMap: Record<string, React.ReactNode> = {
              urgence:               <BadgeAlert className="h-8 w-8" />,
              debouchage:            <Wrench className="h-8 w-8" />,
              "chauffe-eau":         <Flame className="h-8 w-8" />,
              "egouts-drains":       <Droplet className="h-8 w-8" />,
              "detection-fuites":    <Search className="h-8 w-8" />,
              "reparation-tuyaux":   <Wrench className="h-8 w-8" />,
              "inspection-camera":   <Search className="h-8 w-8" />,
              "hydro-jet":           <Waves className="h-8 w-8" />,
              "pompe-puisard":       <Droplet className="h-8 w-8" />,
              "clapet-antiretour":   <ShieldCheck className="h-8 w-8" />,
              "toilettes-accessoires": <Bath className="h-8 w-8" />,
              "tuyaux-geles":        <Snowflake className="h-8 w-8" />,
            };

            return (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {services.filter(s => ["urgence","debouchage","chauffe-eau","detection-fuites","inspection-camera","clapet-antiretour"].includes(s.slug)).map((service) => (
                  <Link key={service.slug} href={`/services/${service.slug}`} className="block h-full group">
                    <Card className={`h-full border-none shadow-md hover:shadow-xl transition-all duration-300 group-hover:-translate-y-1 ${service.isEmergency ? 'bg-primary text-white' : 'bg-white'}`}>
                      <CardContent className="p-8 h-full flex flex-col">
                        <div className={`mb-5 p-4 rounded-xl inline-block w-fit ${service.isEmergency ? 'bg-white/10 text-accent-on-dark' : 'bg-primary/5 text-primary'}`}>
                          {serviceIconMap[service.slug] ?? <Droplet className="h-8 w-8" />}
                        </div>
                        <h3 className={`text-xl font-bold mb-3 ${service.isEmergency ? 'text-white' : 'text-primary'}`}>{service.title}</h3>
                        <p className={`leading-relaxed flex-grow ${service.isEmergency ? 'text-white/80' : 'text-muted-foreground'}`}>
                          {service.description}
                        </p>
                        <div className={`mt-4 flex items-center gap-1 text-sm font-semibold ${service.isEmergency ? 'text-accent-on-dark' : 'text-accent'}`}>
                          En savoir plus <ArrowRight className="h-4 w-4" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            );
          })()}

          <div className="text-center mt-12">
            <Link href="/services" className="inline-flex items-center gap-2 bg-primary text-white font-semibold px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors">
              Voir tous nos services <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <AdditionalServices editorial />
        </div>
      </section>

      <GoogleReviewsTrust />

      {/* 4. POURQUOI NOUS */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">Pourquoi appeler Plomberie Excellence</h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                {BUSINESS_NAME} vous accompagne pour les urgences comme pour les travaux planifiés. Intervention rapide, prix transparents et travail soigné.
              </p>
              <div className="space-y-5">
                {[
                  { title: "Disponible 24h/24, 7j/7", desc: "Urgences ou projets planifiés, nous sommes toujours joignables." },
                  { title: "Transparence", desc: "Vous êtes informé de ce qui doit être fait avant le début des travaux." },
                  { title: "Plombiers certifiés", desc: "Chaque intervention est réalisée par un plombier certifié pour un résultat propre et durable." }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="mt-1 bg-accent/10 p-2 rounded-full h-fit">
                      <CheckCircle className="h-6 w-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-primary mb-1">{item.title}</h3>
                      <p className="text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative aspect-[4/3] lg:aspect-[5/4] overflow-hidden rounded-2xl bg-slate-100 shadow-lg">
              <img
                src={`${import.meta.env.BASE_URL}bathroom-remodel-june-1280.webp`}
                srcSet={`${import.meta.env.BASE_URL}bathroom-remodel-june-640.webp 640w, ${import.meta.env.BASE_URL}bathroom-remodel-june-1280.webp 1280w`}
                sizes="(min-width: 1536px) 720px, (min-width: 1024px) calc((100vw - 112px) / 2), calc(100vw - 48px)"
                alt="Salle de bain avec douche vitrée et meuble-lavabo"
                width={1280}
                height={720}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ZONES DESSERVIES */}
      <ServiceAreas />

      {/* COMMENT ÇA FONCTIONNE */}
      <section className="py-20 md:py-32 bg-primary text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-5 bg-cover bg-center"
          style={{ backgroundImage: `url(${plumberLarge})` }}
          aria-hidden="true"
        />
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Une démarche simple et efficace</h2>
            <p className="text-white/80 text-lg">De votre premier appel jusqu'à la résolution de votre problème, nous rendons l'expérience fluide.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 md:gap-12 relative">
            <div className="hidden md:block absolute top-12 left-[20%] right-[20%] h-0.5 bg-white/20" aria-hidden="true" />
            {[
              { num: "01", title: "Appel ou Formulaire", desc: "Contactez-nous 24/7. Notre équipe prend votre demande en charge dans les plus brefs délais." },
              { num: "02", title: "Évaluation rapide", desc: "Nous posons un diagnostic clair et vous fournissons une estimation transparente." },
              { num: "03", title: "Intervention", desc: "Notre plombier expert résout le problème avec propreté et efficacité." }
            ].map((step) => (
              <div key={step.num} className="relative text-center">
                <div className="w-24 h-24 mx-auto bg-primary border-4 border-white/20 rounded-full flex items-center justify-center text-3xl font-black text-accent-on-dark mb-6 relative z-10 shadow-xl">
                  {step.num}
                </div>
                <h3 className="text-2xl font-bold mb-3">{step.title}</h3>
                <p className="text-white/70 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. FAQ */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">Questions fréquentes</h2>
            <p className="text-lg text-muted-foreground">Les informations utiles avant de nous confier votre intervention.</p>
          </div>
          <NativeFaqList faqs={homeFaqs} />
        </div>
      </section>

      {/* 8. CONTACT FORM */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="flex flex-col gap-8">
            <div className="text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">Contactez-nous rapidement</h2>
              <p className="text-lg text-muted-foreground">
                Remplissez ce court formulaire pour nous décrire votre problème ou votre projet. Nous vous rappellerons dans les plus brefs délais.
              </p>
              
            </div>
            
            <Card className="shadow-lg border-border">
              <CardContent className="p-6 md:p-8">
                {homeSubmitted ? (
                  <div className="py-10 text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="h-8 w-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-primary mb-2">Demande envoyée !</h3>
                    <p className="text-muted-foreground">Nous vous contacterons dans les plus brefs délais.</p>
                  </div>
                ) : (
                  <form className="space-y-6" onSubmit={handleHomeSubmit} aria-busy={homeLoading}>
                    <div className="space-y-2">
                      <label htmlFor="name" className="text-sm font-semibold text-primary">Nom complet</label>
                      <Input id="name" placeholder="Jean Dupont" required className="bg-gray-50 h-12" value={homeName} onChange={e => setHomeName(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="phone" className="text-sm font-semibold text-primary">Numéro de téléphone</label>
                      <Input id="phone" type="tel" placeholder="(514) 555-0123" required className="bg-gray-50 h-12" value={homePhone} onChange={e => setHomePhone(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="message" className="text-sm font-semibold text-primary">Description du problème / projet</label>
                      <Textarea id="message" placeholder="J'ai une fuite sous mon évier de cuisine..." className="bg-gray-50 min-h-[120px] resize-none" value={homeMessage} onChange={e => setHomeMessage(e.target.value)} />
                    </div>
                    <label className="flex items-start gap-3 text-sm leading-relaxed text-foreground/80">
                      <input type="checkbox" checked={homeTerms} onChange={event => setHomeTerms(event.target.checked)} className="mt-1 h-4 w-4 shrink-0 accent-primary" />
                      <span>J’accepte les <a href={`${import.meta.env.BASE_URL}termes-et-conditions/`} target="_blank" rel="noopener noreferrer" className="font-semibold text-primary underline">termes et conditions</a>.</span>
                    </label>
                    <button
                      type="submit"
                      disabled={homeLoading}
                      className="w-full h-14 text-lg font-semibold rounded-md bg-primary text-white cursor-pointer hover:bg-primary/90 disabled:opacity-60 transition-all"
                    >
                      {homeLoading ? "Envoi en cours..." : homeSubmitError ? "Réessayer l’envoi" : "Envoyer la demande"}
                    </button>
                    {homeSubmitError && <p role="alert" data-testid="home-submit-error" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{homeSubmitError}</p>}
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* EMPLACEMENT GOOGLE MAPS */}
      <section className="py-16 md:py-20 bg-slate-50 px-4" aria-labelledby="business-map-heading">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-8 lg:gap-12 items-center">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.16em] text-accent mb-3">Notre emplacement</p>
              <h2 id="business-map-heading" className="text-3xl md:text-4xl font-bold text-primary mb-4">
                {BUSINESS_NAME}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                Consultez la carte pour repérer l’emplacement de l’entreprise et ouvrir l’itinéraire dans Google Maps.
              </p>
              <address className="not-italic flex items-start gap-3 text-foreground font-medium mb-6">
                <MapPin className="h-5 w-5 text-accent mt-0.5 shrink-0" aria-hidden="true" />
                <span>{businessAddressLabel}</span>
              </address>
              <p className="text-muted-foreground mb-6">
                {BUSINESS_HOURS_LABEL} ·{" "}
                <a href={`tel:${BUSINESS_PHONE}`} className="font-semibold text-primary hover:underline">
                  {BUSINESS_PHONE_DISPLAY}
                </a>
              </p>
              <a
                href={businessMapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-primary hover:bg-primary/90 text-white font-semibold px-5 py-3 rounded-md transition-colors"
              >
                Ouvrir dans Google Maps
              </a>
            </div>
            <div className="h-[320px] sm:h-[380px] lg:h-[420px] overflow-hidden rounded-2xl border border-border bg-white shadow-lg">
              <BusinessMap mapUrl={businessMapUrl} title={`Carte Google Maps de ${BUSINESS_NAME}, ${businessAddressLabel}`} />
            </div>
          </div>
        </div>
      </section>

      <QuoteModal open={quoteOpen} onClose={() => setQuoteOpen(false)} />
    </div>
  );
}
